import domToImage from 'dom-to-image';
import html2canvas from 'html2canvas';
import useLayersStore from '../stores/layersStore';

/**
 * Captures a screenshot using dom-to-image (alternative approach)
 * @param {Object} v3dApp - Verge3D app instance from store
 * @param {Object} biocloudGeneratorConfig - Config object with title
 * @param {Function} setScreenshot - Function to set the screenshot data
 * @param {Function} updateProgress - Function to update progress (step, total)
 */
export const captureScreenshotDomToImage = async (v3dApp, biocloudGeneratorConfig, setScreenshot, updateProgress) => {
  try {
    // Step 1: Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    console.log('Viewport dimensions:', viewportWidth, 'x', viewportHeight);
    updateProgress(1, 4);

    // Step 2: Capture background
    const backgroundDiv = document.getElementById('v3d-branding-container');
    if (!backgroundDiv) throw new Error('v3d-branding-container not found');

    const uiSections = [
      document.getElementById('TopSection'),
      document.getElementById('LeftSection'),
      document.getElementById('RightSection'),
      document.getElementById('BottomSection'),
    ].filter(Boolean);
    uiSections.forEach(section => (section.style.display = 'none'));

    // Now that CORS is configured, try using the stored data URL first
    // If that's not available, use html2canvas with CORS support
    const branding = useLayersStore.getState().branding;
    const computedStyle = window.getComputedStyle(backgroundDiv);
    const bgImageStyle = computedStyle.backgroundImage;
    const originalBgImage = backgroundDiv.style.backgroundImage;
    
    // If we have a stored data URL, use it (converted in index.js with CORS)
    // But preserve the gradient/color that comes after the image URL
    if (branding?.backgroundLogoDataURL && bgImageStyle && bgImageStyle !== 'none') {
      const urlMatch = bgImageStyle.match(/url\(['"]?([^'"]+)['"]?\)/);
      if (urlMatch && urlMatch[1] && !urlMatch[1].startsWith('data:')) {
        // Extract everything after the first url() - this includes the gradient/color
        const afterUrl = bgImageStyle.substring(urlMatch[0].length).trim();
        // If there's a comma, keep everything after it (the gradient/color)
        const gradientPart = afterUrl.startsWith(',') ? afterUrl.substring(1).trim() : afterUrl;
        
        // Replace just the URL part, keeping the gradient/color
        const newBackgroundStyle = gradientPart 
          ? `url(${branding.backgroundLogoDataURL}), ${gradientPart}`
          : `url(${branding.backgroundLogoDataURL})`;
        backgroundDiv.style.backgroundImage = newBackgroundStyle;
        console.log('Using stored background logo data URL for screenshot, preserving gradient/color');
        // Small delay to ensure the data URL image loads
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    // Capture background - now with CORS support
    const backgroundCanvas = await html2canvas(backgroundDiv, {
      backgroundColor: null,
      useCORS: true, // Now that CORS is configured, we can use this
      allowTaint: false, // Don't need taint anymore
      scale: 1,
      width: backgroundDiv.offsetWidth,
      height: backgroundDiv.offsetHeight,
      scrollX: 0,
      scrollY: 0,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      logging: false
    });
    
    // Restore original background
    if (originalBgImage !== undefined) {
      backgroundDiv.style.backgroundImage = originalBgImage;
    }
    
    console.log('Background captured, dimensions:', backgroundCanvas.width, 'x', backgroundCanvas.height);
    
    uiSections.forEach(section => (section.style.display = ''));

    // Step 3: Capture 3D model
    updateProgress(2, 4);

    if (!v3dApp) throw new Error('Verge3D app not found');
    const canvas = document.querySelector('#v3d-container canvas');
    if (!canvas) throw new Error('Canvas not found');

    if (v3dApp.renderer?.render) {
      v3dApp.renderer.render(v3dApp.scene, v3dApp.camera);
    }
    const modelDataURL = canvas.toDataURL('image/png');

    // Step 4: Capture labels
    updateProgress(3, 4);
    const labelsDiv = document.getElementById('v3d-anno-container');
    if (!labelsDiv) throw new Error('v3d-anno-container not found');
    console.log('Labels div dimensions:', labelsDiv.offsetWidth, 'x', labelsDiv.offsetHeight);

    // Debug: Log all lines and SVG container details
    const allLines = labelsDiv.querySelectorAll('line');
    console.log('Total lines found:', allLines.length);
    const svgContainer = labelsDiv.querySelector('svg');
    if (svgContainer) {
      const styles = window.getComputedStyle(svgContainer);
      console.log('SVG container styles:', {
        position: styles.position,
        top: styles.top,
        left: styles.left,
        width: styles.width,
        height: styles.height,
      });
    }

    allLines.forEach((line, index) => {
      const computedStyle = window.getComputedStyle(line);
      console.log(`Line ${index}:`, {
        x1: line.getAttribute('x1'),
        y1: line.getAttribute('y1'),
        x2: line.getAttribute('x2'),
        y2: line.getAttribute('y2'),
        stroke: line.getAttribute('stroke'),
        strokeWidth: line.getAttribute('stroke-width'),
        visibility: computedStyle.visibility,
        opacity: computedStyle.opacity,
        display: computedStyle.display,
      });
    });

    // Ensure only lines with non-null stroke are visible
    allLines.forEach(line => {
      const stroke = line.getAttribute('stroke');
      if (stroke && stroke !== 'none' && stroke !== 'null') {
        line.style.visibility = 'visible';
        line.style.opacity = '1';
        line.style.display = 'block';
        if (!line.getAttribute('stroke-width')) {
          line.setAttribute('stroke-width', '2');
        }
      }
    });

    const labelsDataURL = await domToImage.toPng(labelsDiv, {
      width: labelsDiv.offsetWidth,
      height: labelsDiv.offsetHeight,
      bgcolor: null,
      quality: 1,
      scale: 3,
      filter: node => {
        if (node.tagName === 'LINK' && node.href.includes('fonts.googleapis.com')) {
          return false;
        }
        return true;
      },
    });

    // Debug: Check captured labels
    const debugCanvas = document.createElement('canvas');
    const debugImg = new Image();
    debugImg.src = labelsDataURL;
    await new Promise(resolve => (debugImg.onload = resolve));
    debugCanvas.width = debugImg.width;
    debugCanvas.height = debugImg.height;
    const debugCtx = debugCanvas.getContext('2d');
    debugCtx.drawImage(debugImg, 0, 0);
    const debugData = debugCtx.getImageData(0, 0, debugCanvas.width, debugCanvas.height).data;
    let nonTransparentPixels = 0;
    for (let i = 3; i < debugData.length; i += 4) {
      if (debugData[i] > 0) nonTransparentPixels++;
    }
    console.log('Non-transparent pixels in labels:', nonTransparentPixels);

    // Step 5: Capture title
    let titleCanvas = null;
    if (biocloudGeneratorConfig?.title) {
      const titleText = biocloudGeneratorConfig.title;
      titleCanvas = document.createElement('canvas');
      const titleCtx = titleCanvas.getContext('2d');
      titleCtx.font = '16px Arial';
      const textMetrics = titleCtx.measureText(titleText);
      titleCanvas.width = textMetrics.width + 20;
      titleCanvas.height = 30;
      titleCtx.fillStyle = '#ffffff';
      titleCtx.fillText(titleText, 10, 20);
    }

    // Step 6: Merge elements
    const compositeCanvas = document.createElement('canvas');
    compositeCanvas.width = viewportWidth;
    compositeCanvas.height = viewportHeight;
    const compositeCtx = compositeCanvas.getContext('2d');

    // Draw background canvas directly - we can draw a tainted canvas onto a clean canvas!
    // This works because we're copying pixels, not reading them
    compositeCtx.drawImage(backgroundCanvas, 0, 0);

    const modelImage = new Image();
    modelImage.src = modelDataURL;
    await new Promise(resolve => (modelImage.onload = resolve));
    compositeCtx.drawImage(modelImage, 0, 0);

    const labelsImage = new Image();
    labelsImage.src = labelsDataURL;
    await new Promise(resolve => (labelsImage.onload = resolve));

    // Fallback: Manually draw lines with non-null stroke
    allLines.forEach(line => {
      const stroke = line.getAttribute('stroke');
      if (stroke && stroke !== 'none' && stroke !== 'null') {
        // Create temporary rects to get bounding box
        const startPoint = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        startPoint.setAttribute('x', line.getAttribute('x1') || '0');
        startPoint.setAttribute('y', line.getAttribute('y1') || '0');
        startPoint.setAttribute('width', '0');
        startPoint.setAttribute('height', '0');
        const endPoint = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        endPoint.setAttribute('x', line.getAttribute('x2') || '0');
        endPoint.setAttribute('y', line.getAttribute('y2') || '0');
        endPoint.setAttribute('width', '0');
        endPoint.setAttribute('height', '0');

        const svgContainer = line.closest('svg');
        if (!svgContainer) {
          console.warn('No SVG container found for line');
          return;
        }
        svgContainer.appendChild(startPoint);
        svgContainer.appendChild(endPoint);

        // Get viewport coordinates
        const startRect = startPoint.getBoundingClientRect();
        const endRect = endPoint.getBoundingClientRect();

        // Remove temporary points
        svgContainer.removeChild(startPoint);
        svgContainer.removeChild(endPoint);

        const x1 = startRect.left;
        const y1 = startRect.top;
        const x2 = endRect.left;
        const y2 = endRect.top;

        // Skip if coordinates are invalid
        if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) {
          console.warn(`Skipping line due to invalid coordinates: x1=${x1}, y1=${y1}, x2=${x2}, y2=${y2}, value_x1=${line.getAttribute('x1')}, value_y1=${line.getAttribute('y1')}, value_x2=${line.getAttribute('x2')}, value_y2=${line.getAttribute('y2')}`);
          return;
        }

        console.log(`Drawing line: (${x1}, ${y1}) to (${x2}, ${y2}), stroke: ${stroke}, strokeWidth: ${line.getAttribute('stroke-width')}`);

        compositeCtx.beginPath();
        compositeCtx.moveTo(x1, y1);
        compositeCtx.lineTo(x2, y2);
        compositeCtx.strokeStyle = stroke;
        compositeCtx.lineWidth = parseFloat(line.getAttribute('stroke-width')) || 2;
        compositeCtx.stroke();
      }
    });
    compositeCtx.drawImage(labelsImage, 0, 0);

    if (titleCanvas) {
      compositeCtx.drawImage(titleCanvas, 0, 0);
    }

    const finalDataURL = compositeCanvas.toDataURL('image/png');
    setScreenshot(finalDataURL);
  } catch (error) {
    console.error('Screenshot failed:', error);
    throw error;
  }
};